import React, { useMemo } from 'react';
import { ClientInvitation } from '../../../../../hooks/api/useMyOrganizationClientInvitations';
import { getIntl } from '../../../../../utils/intl';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import ConsentFile from './ConsentFile';
import StepPaper from '../StepPaper';
import GateButtons from '../GateButtons';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { colors } from '../../../../../utils/theme';
import { isConsentCheckboxInput, isFileInput } from '../../../../../hooks/api/useOrganizationInvitationDefinitions';
import { APITypes, OrganizationTypes } from '../../../../../primary-record-types';
import { Field, FieldArray, getIn } from 'formik';
import { Form } from '../../../../v2BaseComponents/Form';
import { acceptanceCheck, required } from '../../../../../utils/validators';
import Icon from '../../../../v2BaseComponents/Icon';
import Button from '../../../../v2BaseComponents/Button';
import { useTempFilePicker } from '../../../../v2BaseComponents/ImagePicker/TempFilePicker';
import { FormikFieldProps } from '../../../../v2BaseComponents/Inputs/FormikInputs';
import { useIsDisabledByParent } from '../../../../v2BaseComponents/IsDisabledProvider';
import SectionHeader from '../../../../v2BaseComponents/SectionHeader';

const intl = getIntl('organization-client-invitation-consent-step', {
    headerText:
        "{organizationName} has requested access to {clientName}'s profile.  Please review and accept the consent to continue.",
    continue: 'Continue',
    paperTitle: 'Additional Consents',
    consentDocuments: 'Consent Documents',
    fileUpload: 'File Uploads',
    removeFile: 'Remove File',
    uploadFile: 'Upload File',
});

export type NullableFileInput = Omit<
    APITypes.organizationService.OrganizationClientInvitationFileInput,
    'tempFileId' | 'contentType'
> & {
    tempFileId: string | null;
    contentType: string | null;
};

export type OnConsentCompleteArgs = {
    fileInputs: NullableFileInput[];
};

export type ConsentStepOwnProps = {
    clientInvitation: ClientInvitation;
    onConsentComplete: (args: OnConsentCompleteArgs) => void;
    onReject: () => void;
};

export type ConsentStepProps = ConsentStepOwnProps;

const ExistingFileDisplay: React.FC<{ onClear: () => void; isDisabled: boolean; index: number }> = (props) => (
    <Stack vertical fullWidth justify="center" align="center">
        <Icon
            name="FileText"
            size="selected-file-icon"
            testID={`client-invitation-file-upload-${props.index}-display`}
        />
        <Button
            variant="text-dark"
            onPress={props.onClear}
            isDisabled={props.isDisabled}
            title={intl('removeFile')}
            icon="Trash2"
            testID={`delete-file-${props.index}`}
        />
    </Stack>
);

const UploadFileInput: React.FC<{
    onUpload: (tempFileId: string, contentType: string) => void;
    isDisabled: boolean;
    index: number;
}> = (props) => {
    const { isLoadingFile, onPress, E2EInput } = useTempFilePicker({
        testID: `client-invitation-file-upload-${props.index}`,
        onPick: (value) => {
            if (value?.tempFileId) {
                props.onUpload(value.tempFileId, value.contentType);
            }
        },
    });

    return (
        <Stack vertical fullWidth justify="center" align="center">
            {E2EInput}
            <Button
                variant="secondary"
                onPress={onPress}
                isDisabled={props.isDisabled || isLoadingFile}
                title={intl('uploadFile')}
                icon="Upload"
            />
        </Stack>
    );
};

const FormikFieldUploadFileInput: React.FC<
    FormikFieldProps<{}> & {
        file: OrganizationTypes.InvitationDefinitionInputFile;
        index: number;
        contentTypeName: string;
    }
> = (props) => {
    const isDisabled = useIsDisabledByParent();
    const validationError = getIn(props.form.errors, props.field.name);
    const touched = getIn(props.form.touched, props.field.name);
    const error = touched && validationError ? validationError : null;

    return (
        <Stack vertical fullWidth space={1}>
            <Stack
                vertical
                fullWidth
                style={[
                    { backgroundColor: colors.Background, borderRadius: 8 },
                    error
                        ? {
                              borderWidth: 1,
                              borderColor: colors.Alert,
                          }
                        : {},
                ]}
                padding={4}
                space={2}
            >
                <Text variant="body-1">{`${props.file.name}${props.file.isRequired ? '*' : ''}`}</Text>
                {props.field.value ? (
                    <ExistingFileDisplay
                        onClear={() => {
                            props.form.setFieldValue(props.field.name, null);
                            props.form.setFieldValue(props.contentTypeName, null);
                        }}
                        isDisabled={isDisabled}
                        index={props.index}
                    />
                ) : (
                    <UploadFileInput
                        onUpload={(tempFileId, contentType) => {
                            props.form.setFieldValue(props.field.name, tempFileId);
                            props.form.setFieldValue(props.contentTypeName, contentType);
                        }}
                        isDisabled={isDisabled}
                        index={props.index}
                    />
                )}
                {props.file.description ? <Text variant="caption-1">{props.file.description}</Text> : null}
            </Stack>
            {error ? <Text variant="input-error">{error}</Text> : null}
        </Stack>
    );
};

type FormValue = {
    checkboxInputs: boolean[];
    fileInputs: NullableFileInput[];
};

const ConsentStep: React.FC<ConsentStepProps> = ({ clientInvitation, onConsentComplete, onReject }) => {
    const checkboxInputs = useMemo(() => clientInvitation.inputs.filter(isConsentCheckboxInput), [clientInvitation]);
    const fileInputs = useMemo(() => clientInvitation.inputs.filter(isFileInput), [clientInvitation]);
    const defaultFormValues: FormValue = useMemo<FormValue>(
        () => ({
            checkboxInputs: checkboxInputs.map(() => false),
            fileInputs: fileInputs.map((file) => ({
                fileInputId: file.fileInputId,
                tempFileId: null,
                contentType: null,
            })),
        }),
        [checkboxInputs, fileInputs],
    );

    return (
        <StepPaper stepIndex={2} stepTitle={intl('paperTitle')}>
            <Form
                initialValues={defaultFormValues}
                onSubmit={(values) => {
                    onConsentComplete({
                        fileInputs: values.fileInputs,
                    });
                }}
            >
                {({ values, handleSubmit }) => (
                    <MainContentContainer>
                        <Stack vertical fullWidth space={8}>
                            <Stack vertical fullWidth space={contentStackSpacing}>
                                <Text variant="body-1">
                                    {intl('headerText', {
                                        organizationName: clientInvitation.organization.metadata.name,
                                        clientName: `${clientInvitation.clientFirstName} ${clientInvitation.clientLastName}`,
                                    })}
                                </Text>
                                {clientInvitation.inputs.length ? (
                                    <FieldArray
                                        name="checkboxInputs"
                                        render={() => (
                                            <Stack vertical fullWidth space={contentStackSpacing}>
                                                {values.checkboxInputs.map((_, index) => (
                                                    <Field
                                                        key={index}
                                                        validate={acceptanceCheck}
                                                        name={`checkboxInputs.${index}`}
                                                        component={FormikInputs.Checkbox}
                                                        testID={`consent-checkbox-${checkboxInputs[index].label}`}
                                                        title={checkboxInputs[index].label}
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                    />
                                ) : null}
                            </Stack>
                            {clientInvitation.files.length ? (
                                <Stack vertical fullWidth space={contentStackSpacing}>
                                    <SectionHeader text={intl('consentDocuments')} />
                                    <Stack vertical fullWidth space={3}>
                                        {clientInvitation.files.map((file, index) => (
                                            <ConsentFile key={index} file={file} invitation={clientInvitation} />
                                        ))}
                                    </Stack>
                                </Stack>
                            ) : null}
                            {values.fileInputs.length ? (
                                <Stack vertical fullWidth space={contentStackSpacing}>
                                    <SectionHeader text={intl('fileUpload')} />
                                    <Stack vertical fullWidth space={3}>
                                        <FieldArray
                                            name="fileInputs"
                                            render={() => (
                                                <Stack vertical fullWidth space={contentStackSpacing}>
                                                    {values.fileInputs.map((_, index) => {
                                                        const indexInputFileId = values.fileInputs[index].fileInputId;
                                                        const targetInputFile = fileInputs.find(
                                                            (file) => file.fileInputId === indexInputFileId,
                                                        );
                                                        if (!targetInputFile) {
                                                            return null;
                                                        }

                                                        return (
                                                            <Field
                                                                key={index}
                                                                name={`fileInputs.${index}.tempFileId`}
                                                                component={FormikFieldUploadFileInput}
                                                                file={targetInputFile}
                                                                index={index}
                                                                validate={
                                                                    targetInputFile.isRequired ? required : undefined
                                                                }
                                                                contentTypeName={`fileInputs.${index}.contentType`}
                                                            />
                                                        );
                                                    })}
                                                </Stack>
                                            )}
                                        />
                                    </Stack>
                                </Stack>
                            ) : null}
                            <GateButtons
                                primaryButtonProps={{
                                    variant: 'primary',
                                    title: intl('continue'),
                                    onPress: () => handleSubmit(),
                                    icon: 'ArrowRightCircle',
                                    testID: 'consent-step-continue-button',
                                }}
                                secondaryButtonProps={{
                                    variant: 'secondary',
                                    title: 'Reject',
                                    onPress: onReject,
                                    icon: 'XCircle',
                                    testID: 'consent-step-reject-button',
                                }}
                            />
                        </Stack>
                    </MainContentContainer>
                )}
            </Form>
        </StepPaper>
    );
};

export default React.memo(ConsentStep);
